import { KitStockComponent } from "src/app/acumatica/EntityServices/KitSpecification.service";

export enum KitAuditType {
    Binders,
    LooseCards,
    CollatedSets
}

export class KitAudit {
    constructor(
        type: KitAuditType,
        kits: KitAuditSet[] = []
    ) {
        this.type = type;
        this.kits = kits;
    }

    public serialize(): string {
        // clone the KitAudit into a plain object
        let data = <any>Object.assign({}, this);
        
        // modify the plain object for serialization
        delete data._auditKitIndex;

        data.notices = {wrongKit:[]};

        this.notices.forEach(notice => {
            let noticeContainerArray: Array<string> | null | undefined = undefined;

            if(notice instanceof KitAuditNotice_ItemInWrongKit) noticeContainerArray = data.notices.wrongKit;
            if(notice instanceof KitAuditNotice_InpersistentMessage) noticeContainerArray = null;

            if(noticeContainerArray === undefined) {
                console.error("KitAuditNotice type is not configured in KitAudit serialization", notice);
                throw new Error("KitAuditNotice type is not configured in KitAudit serialization");
            }

            if(noticeContainerArray != null) {
                let serializedNotice = notice.serialize(this);
                if(serializedNotice != null) noticeContainerArray.push(serializedNotice);
            }
        });

        return JSON.stringify(data);
    }

    public static deserialize(data: string): KitAudit {
        let obj = JSON.parse(data);

        // cast plain object into KitAudit instance
        let audit = Object.assign(new KitAudit(0), obj);

        // cast plain objects into KitAuditSet instances
        audit.kits = audit.kits.map((kit: any) => {
            let typedkit = Object.assign(new KitAuditSet(), kit);
            // cast plain objects into `KitAuditItem` instances
            typedkit.items = typedkit.items.map((item: any) => Object.assign(new KitAuditItem(), item));
            return typedkit;
        });

        // cast plain objects into KitAuditNotices
        let noticesArray: any[] = [];
        Object.keys(audit.notices).forEach(type => {
            let notices = audit.notices[type];
            switch(type) {
                case 'wrongKit':
                    noticesArray = noticesArray.concat(notices.map((notice: string) => new KitAuditNotice_ItemInWrongKit(notice, audit)));
                    break;
                default:
                    throw new Error("Notice type '" + type + "' is not configured in KitAudit deserialization");
            }
        });
        audit.notices = noticesArray;

        return audit;
    }

    /** the type of kits included in the audit operation */
    public type: KitAuditType;

    /** the collection of all kits in the audit operation */
    public kits: KitAuditSet[];
    
    /** the collection notices to the user regarding audit progress */
    public notices: KitAuditNotice[] = [];

    /** a flag the user can toggle to indicate if they are placing empty binder shells into the customer library */
    public anyBinderShellsPlaced: boolean = false;
    /** an object map of binder shells and a boolean flag indicating for the user to specify if they were placed or not */
    public placedEmptyShells: {[key:string]: boolean} = {};    
    /** returns an array of placed binder shell Inventory IDs */
    public placedEmptyShellsList(): string[] {
        if(!this.anyBinderShellsPlaced) return [];
        return Object.keys(this.placedEmptyShells)
            .filter(id => this.placedEmptyShells[id]);
    }
    
    /** the kit actively being audited */
    public get activeKit(): KitAuditSet | undefined { return this._auditKitIndex===undefined ? undefined : this.kits[this._auditKitIndex]; }
    public set activeKit(value: KitAuditSet | undefined) { this._auditKitIndex = value === undefined ? undefined : this.kits.indexOf(value); }
    // internally tracked using kit index number for efficient (de-)serialization
    private _auditKitIndex?: number;
    
    /** returns a boolean value indicating if the audit is a CollatedSets type audit */
    public IsMemoAudit(): boolean { return this.type == KitAuditType.CollatedSets; }
    /** returns a booean value indicating if the audit is a standard Binder audit */
    public IsBinderAudit(): boolean { return this.type == KitAuditType.Binders; }
    /** returns a boolean value indicating if the audit is a Binder or Loose Card type audit */
    public IsCardAudit(): boolean { return this.type == KitAuditType.Binders || this.type == KitAuditType.LooseCards; }
    /** returns a boolean value indicating if any sets have had any items audited */
    public AnyChecked(): boolean { return this.kits.reduce<boolean>((prev, kit) => prev || kit.AnyChecked(), false); }
}

/** a set of items (Kit) containing the various component items (KitAuditItem) to be audited */
export class KitAuditSet {
    public Description!: string;
    public KitInventoryID?: string;
    public items!: KitAuditItem[];

    constructor()
    constructor(Description: string, items: KitAuditItem[], KitInventoryID?: string);
    constructor(Description?: string, items?: KitAuditItem[], KitInventoryID?: string) {
        if(Description !== undefined) this.Description = Description;
        if(items !== undefined) this.items = items;
        this.KitInventoryID = KitInventoryID;
    }

    /** return an array of components that have been checked */
    public Checked(): KitAuditItem[] { return this.items.filter(item => item.checked); }
    /** return an array of components that have not been checked */
    public Unchecked(): KitAuditItem[] { return this.items.filter(item => !item.checked); }
    /** return a boolean value indicating if this Kit has any component items checked */
    public AnyChecked(): boolean { return this.Checked().length > 0 }
    /** return a boolean value indicating if this Kit has all component items checked */
    public AllChecked(): boolean { return this.Checked().length == this.items.length; }
    /** return a boolean value indicating if this Kit is partially checked */
    public PartiallyChecked(): boolean { let count = this.Checked().length; return count > 0 && count < this.items.length; }
}

/** a specific component item within a Kit to be audited */
export class KitAuditItem {

    /** convert KitStockComponent object into a KitAuditItem */
    static fromKitStockComponent(item: KitStockComponent): KitAuditItem {
        return new KitAuditItem({
            InventoryID: item.InventoryID,
            Description: item.Description || '',
            LifeCycle: item.LifeCycle
        });
    }

    /** convert a 'Collated Set by Inventory ID' G.I. result into a KitAuditItem */
    static fromCollatedSetGenericInquiry(item: any): KitAuditItem {
        return new KitAuditItem({
            InventoryID: item.InventoryID,
            Description: item.ColorName,
            LifeCycle: item.LifeCycle,
        });
    }

    public InventoryID!: string;
    public Description!: string;
    public LifeCycle!: string | null;

    public checked: boolean = false;

    constructor(init?: Partial<KitAuditItem>) { Object.assign(this, init); }
}

export abstract class KitAuditNotice {
    public abstract serialize(kitAudit: KitAudit): string | null;
    public abstract deserialize(data: string, kitAudit: KitAudit): void;
    public abstract toString(): string;
}

export class KitAuditNotice_InpersistentMessage extends KitAuditNotice {
    public message: string;

    constructor(message: string) {
        super();

        this.message = message;
    }

    public toString() { return this.message; }
    public serialize(kitAudit: KitAudit): string | null { return null; }
    public deserialize(data: string, kitAudit: KitAudit): void { }
}

export class KitAuditNotice_ItemNotFound extends KitAuditNotice {
    public InventoryID: string;

    constructor(InventoryID: string) {
        super();

        this.InventoryID = InventoryID;
    }

    public toString() { return this.InventoryID; }
    public serialize(kitAudit: KitAudit): string | null { return null; }
    public deserialize(data: string, kitAudit: KitAudit): void { }
}

export class KitAuditNotice_ItemInWrongKit extends KitAuditNotice {
    public InventoryID!: string;
    public ItemDescr!: string;
    public FoundInKit!: KitAuditSet;
    public CorrectKit!: KitAuditSet;
    
    constructor(serialData: string, audit: KitAudit);
    constructor(InventoryID: string, ItemDescr: string, FoundInKit: KitAuditSet, CorrectKit: KitAuditSet)
    constructor(p1: string, p2: string | KitAudit, FoundInKit?: KitAuditSet, CorrectKit?: KitAuditSet) {
        super();

        if(FoundInKit) {
            this.InventoryID = p1;
            this.ItemDescr = <string>p2;
            this.FoundInKit = FoundInKit!;
            this.CorrectKit = CorrectKit!;
        } else {
            this.deserialize(p1, <KitAudit>p2);
        }
    }

    public toString() {
        return `<b>${this.InventoryID} - ${this.ItemDescr}</b> was scanned in <b>${this.FoundInKit.Description}</b>\nIt belongs in <b>${this.CorrectKit.Description}</b>`;
    }

    public serialize(kitAudit: KitAudit) {
        let data = {
            id: this.InventoryID,
            descr: this.ItemDescr,
            from: kitAudit.kits.indexOf(this.FoundInKit),
            to: kitAudit.kits.indexOf(this.CorrectKit)
        };
        return JSON.stringify(data);
    }
    public deserialize(data: string, kitAudit: KitAudit) {
        let obj = JSON.parse(data);
        this.InventoryID = obj.id;
        this.ItemDescr = obj.descr;
        this.FoundInKit = kitAudit.kits[obj.from];
        this.CorrectKit = kitAudit.kits[obj.to];
    }
}